import { useEffect, useRef } from "react";

/**
 * useDebouncedAutosave
 * Triggers callback after value changes and delay passes without further changes.
 * Returns cancel method via ref for manual flush if needed.
 */
export default function useDebouncedAutosave(value, callback, delay = 1000) {
  const timer = useRef(null);
  const latestCallback = useRef(callback);
  const latestValue = useRef(value);

  // Keep refs up to date
  useEffect(() => {
    latestCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    latestValue.current = value;
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      latestCallback.current(latestValue.current);
    }, delay);

    return () => clearTimeout(timer.current);
  }, [value, delay]);

  // return cancel & flush helpers
  return {
    cancel: () => timer.current && clearTimeout(timer.current),
    flush: () => {
      if (timer.current) clearTimeout(timer.current);
      latestCallback.current(latestValue.current);
    },
  };
}